import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router-dom';
import {ConfigProvider, Layout, message, Spin} from 'antd';
import Sidebar from './Components/Sidebar';
import Dashboard from './Pages/Dashboard';
import ActivityHistory from './Pages/ActivityHistory';
import UserPage from './Pages/UserPage';
import {ArchivedScoresPage as StatisticsPage} from "./Pages/StatisticPage";
import AddActivityModal from "./Components/AddActivityModal";
import {UsernatorClient} from "./generated/user_grpc_web_pb";
import {UserData} from "./generated/user_pb";
import GRPC_BACKEND_URL, {dowtexHubUrl, GRPC_CLIENT_OPTIONS, REGISTRATION_ENABLED, SSO_ENABLED} from "./grpcConfig";
import {ActivitatorClient} from "./generated/activity_grpc_web_pb";
import {Activity, ActivityTemplate} from "./generated/activity_pb";
import {useOutboxSync} from '@dowty/offline';
import {encodeMessage, enqueueActivity, NO_SPORTTYPES, outbox, useSporttypes, useTemplates} from './offline';
import TemplateOverviewPage from "./Pages/TemplatesOverview";
import { DesignProvider, sportdings, useColorMode } from '@dowty/ui';
import { ImpressumPage, PrivacyPage } from '@dowty/legal';
import RouterLegalFooter from './Components/RouterLegalFooter';
import LoginPage from './Pages/LoginPage';
import RegisterPage from './Pages/RegisterPage';
import {isGerman} from './utils/language';
import {isNetworkError} from '@dowty/core';
import deDE from 'antd/locale/de_DE';
import enUS from 'antd/locale/en_US';




export type activityTemplate = {
    templateName: string,
    activities: Activity[]
}

export type insertUpdateAction = {
    sport: string | null;
    date: number | string;
    parameterOne: number;
    parameterTwo: number;
    id: number | string;
    templateName?: string;
}



const buildDowtexLoginUrl = () =>
    `${dowtexHubUrl()}/login?redirect=${encodeURIComponent(window.location.origin)}`;

const startSso = (promptNone: boolean) => {
    const page = new URL(window.location.href);
    page.searchParams.delete('sso');
    page.searchParams.delete('sso_error');
    const returnTo = `${page.pathname}${page.search}${page.hash}`;
    const safeReturnTo = returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.includes('\\') && !Array.from(returnTo).some((c) => c.codePointAt(0)! < 32 || c.codePointAt(0) === 127)
        ? returnTo
        : '/';
    // A GET form replaces the action's query string, so the parameters travel as fields.
    const form = document.createElement('form');
    form.method = 'get';
    form.action = new URL('/auth/sso/start', GRPC_BACKEND_URL).toString();
    form.hidden = true;
    const fields: Record<string, string> = promptNone ? {returnTo: safeReturnTo, prompt: 'none'} : {returnTo: safeReturnTo};
    for (const [name, value] of Object.entries(fields)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.append(input);
    }
    document.body.append(form);
    form.submit();
};

const readCookieUser = (): string | null => {
    const match = document.cookie.split('; ').find(r => r.startsWith('dowtex-auth='));
    if (!match) return null;
    try {
        const cookieUser = JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')));
        if (!cookieUser?.token) return null;
        const userData = new UserData();
        userData.setUserid(cookieUser.userId);
        userData.setUsername(cookieUser.username);
        userData.setToken(cookieUser.token);
        const serialized = encodeMessage(userData);
        localStorage.setItem('user', serialized);
        return serialized;
    } catch {
        return null;
    }
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; onAuthenticated: () => void }> = ({ children, onAuthenticated }) => {
    const [isAuthChecked, setIsAuthChecked] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const handleAuthExpired = () => {
            if (!SSO_ENABLED) {
                localStorage.removeItem('user');
                window.location.href = buildDowtexLoginUrl();
                return;
            }
            sessionStorage.removeItem('sportdings-sso-session');
            sessionStorage.setItem('dowtex-sso-interactive', '1');
            setIsAuthenticated(false);
            setIsAuthChecked(true);
        };
        window.addEventListener('sportdings:auth-expired', handleAuthExpired);
        return () => window.removeEventListener('sportdings:auth-expired', handleAuthExpired);
    }, []);

    useEffect(() => {
        const search = new URLSearchParams(window.location.search);
        if (search.get('sso') === 'none') {
            search.delete('sso');
            const query = search.toString() ? '?' + search : '';
            window.history.replaceState(null, '', `${window.location.pathname}${query}${window.location.hash}`);
            if (sessionStorage.getItem('dowtex-sso-silent') !== 'signed-out') {
                sessionStorage.setItem('dowtex-sso-silent', 'none');
            }
        }

        const error = new URLSearchParams(window.location.search).get('sso_error');
        if (error) {
            const url = new URL(window.location.href);
            url.searchParams.delete('sso_error');
            window.history.replaceState(null, '', url.pathname + url.search + url.hash);
            const messages: Record<string, [string, string]> = {
                invalid_state: ['Die Anmeldung ist abgelaufen. Bitte erneut versuchen.', 'Sign-in expired. Please try again.'],
                denied: ['Die Anmeldung wurde abgebrochen.', 'Sign-in was cancelled.'],
                invalid_grant: ['Die Anmeldung konnte nicht bestätigt werden.', 'Sign-in could not be confirmed.'],
            };
            const [de, en] = messages[error] ?? ['Der Anmeldedienst ist vorübergehend nicht verfügbar.', 'Sign-in is temporarily unavailable.'];
            message.error(isGerman() ? de : en);
        }

        const userString = localStorage.getItem('user') ?? readCookieUser();
        const user = userString
            ? UserData.deserializeBinary(Uint8Array.from(atob(userString), c => c.codePointAt(0)!))
            : new UserData();
        const client = new UsernatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);
        const finish = (err: unknown, res: UserData | null) => {
            if (err || !res) {
                if (userString && isNetworkError(err)) {
                    setIsAuthChecked(true);
                    setIsAuthenticated(true);
                    onAuthenticated();
                    return;
                }
                if (SSO_ENABLED) {
                    localStorage.removeItem('user');
                    sessionStorage.removeItem('sportdings-sso-session');
                    const silentStatus = sessionStorage.getItem('dowtex-sso-silent');
                    if (silentStatus === null && sessionStorage.getItem('dowtex-sso-interactive') === null) {
                        sessionStorage.setItem('dowtex-sso-silent', 'tried');
                        startSso(true);
                        return;
                    }
                    if ((silentStatus === 'tried' || silentStatus === 'none') && sessionStorage.getItem('dowtex-sso-interactive') === null) {
                        sessionStorage.setItem('dowtex-sso-interactive', '1');
                        startSso(false);
                        return;
                    }
                } else {
                    window.location.href = buildDowtexLoginUrl();
                    return;
                }
                setIsAuthChecked(true);
                return;
            }
            setIsAuthChecked(true);
            setIsAuthenticated(true);
            sessionStorage.removeItem('dowtex-sso-silent');
            sessionStorage.removeItem('dowtex-sso-interactive');
        };

        if (SSO_ENABLED && !user.getToken()) {
            client.getUserInfo(user, undefined, (err, res) => {
                finish(err, res);
                if (!err && res) {
                    localStorage.setItem('user', encodeMessage(res));
                    sessionStorage.setItem('sportdings-sso-session', 'true');
                    onAuthenticated();
                }
            });
        } else if (userString) {
            client.validateSession(user, undefined, finish);
        } else {
            finish(new Error('No signed-in user'), null);
        }
    }, [onAuthenticated]);

    if (!isAuthChecked) {
        return <output><Spin size="small" /> {isGerman() ? 'Anmeldung wird geprüft …' : 'Checking your sign-in …'}</output>;
    }

    if (isAuthenticated) {
        return <>{children}</>;
    }
    return SSO_ENABLED ? <LoginPage onLogin={() => { sessionStorage.removeItem('dowtex-sso-silent'); sessionStorage.removeItem('dowtex-sso-interactive'); setIsAuthenticated(true); onAuthenticated(); }} onRetry={() => { sessionStorage.removeItem('dowtex-sso-interactive'); startSso(false); }} /> : null;
};


const SportdingsApp: React.FC = () => {
    const usernatorClient = useMemo(() => new UsernatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS), []);
    const activityClient = useMemo(() => new ActivitatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS), []);
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const sportTypes = useSporttypes().data ?? NO_SPORTTYPES;
    const templatesData = useTemplates().data;
    const templates = useMemo<activityTemplate[] | undefined>(() => templatesData?.getActivitietemplatesList().map(template => ({
        templateName: template.getTemplatename(),
        activities: template.getActivitiesList(),
    })), [templatesData]);
    useOutboxSync(outbox);

    const [toReload, setToReload] = useState<number>(0);
    const onAuthenticated = useCallback(() => setToReload(value => value + 1), []);
    const { mode: theme, setSetting } = useColorMode('theme', sportdings);

    const toggleTheme = () => {
        setSetting(theme === 'light' ? 'dark' : 'light');
    };

    const reloadEverything = useCallback(() => {
        const userString = localStorage.getItem('user') ?? readCookieUser();
        if(userString == null){
            return;
        }
        const user = UserData.deserializeBinary(Uint8Array.from(atob(userString), c =>c.codePointAt(0)!));
        setCurrentUser(user);
        usernatorClient.validateSession(user, undefined, (err, res) => {
            if (err) {
                message.error(isGerman() ? 'Die Sitzung konnte nicht bestätigt werden. Bitte erneut anmelden.' : 'Could not validate your session. Please sign in again.');
                if (!isNetworkError(err)) localStorage.removeItem('user');
            }
            if (res) {
                localStorage.setItem('user', encodeMessage(res));
            }
        })
        usernatorClient.getFriends(user, undefined, (err, res) =>{
            if (err) {
                message.error(isGerman() ? 'Freunde konnten nicht geladen werden.' : 'Could not load friends.');
                localStorage.removeItem('friends')
            }
            if (res) {
                localStorage.setItem('friends', encodeMessage(res));
            }
        })
        usernatorClient.getAllUsers(user, undefined, (err, res) =>{
            if (err) {
                message.error(isGerman() ? 'Benutzer konnten nicht geladen werden.' : 'Could not load users.');
                localStorage.removeItem('allUsers')
            }
            if (res) {
                localStorage.setItem('allUsers', encodeMessage(res));
            }
        })
    }, [usernatorClient]);
    useEffect(() => {
        reloadEverything()
    }, [toReload, reloadEverything]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const handleAddActivityClick = () => {
        setIsModalVisible(true);  // Show the modal
    };

    const handleModalOk = (data: insertUpdateAction|activityTemplate) =>
        'activities' in data ? handleTemplateAction(data) : handleActivityAction(data);

    function handleTemplateAction(data: activityTemplate): Promise<void>{
        let ret = new ActivityTemplate()
        ret.setUser(currentUser!);
        ret.setTemplatename(data.templateName)
        ret.setActivitiesList(data.activities)
        return new Promise((resolve, reject) => {
            activityClient.insertActivities(ret, undefined, (err, res) => {
                if (err) {
                    reject(err);
                    return;
                }
                setIsModalVisible(false);
                setToReload(value => value + 1);
                resolve();
            })
        })
    }

    const handleActivityAction = async (data: insertUpdateAction) => {
        await enqueueActivity(sportTypes, data);
        setIsModalVisible(false);
    };

    const isDark = theme === 'dark';
    const layoutBackground = 'var(--ui-bg)';
    const contentBackground = 'var(--ui-surface)';

    const authenticatedLayout = (
                <Sidebar onAddActivityClick={handleAddActivityClick} isDark={isDark} toggleTheme={toggleTheme} username={currentUser?.getUsername()}>
                <Layout style={{ flex: 1, background: contentBackground }}>
                    <div className="sf-page-content">
                        <Routes>
                            <Route path="/dashboard" element={<Dashboard key={toReload} onAddActivityClick={handleAddActivityClick} />} />
                            <Route path="/history" element={<ActivityHistory onAddActivityClick={handleAddActivityClick} />} />
                            <Route path="/user" element={<UserPage />} />
                            <Route path="/statistics" element={<StatisticsPage theme={theme} />} />
                            <Route path="/templates" element={<TemplateOverviewPage/>} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </div>
                </Layout>
                <AddActivityModal
                    isOpen={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={handleModalOk}
                    mode="add"
                    onDelete={() => setIsModalVisible(false)}
                    actualSportTypes={sportTypes}
                    isTemplateCreation={false}
                    activityTemplate={templates}
                />
                </Sidebar>
    );


    return (
        <ConfigProvider locale={isGerman() ? deDE : enUS}>
            <Router>
                <Layout style={{ minHeight: '100vh', background: layoutBackground }}>
                    <Routes>
                        <Route path="/register" element={REGISTRATION_ENABLED ? <RegisterPage /> : <Navigate to="/" replace />} />
                        <Route path="/impressum" element={<><ImpressumPage app="sportdings" /><RouterLegalFooter /></>} />
                        <Route path="/datenschutz" element={<><PrivacyPage app="sportdings" /><RouterLegalFooter /></>} />
                        <Route path="/" element={
                            <ProtectedRoute onAuthenticated={onAuthenticated}>
                                <Navigate to="/dashboard" replace />
                            </ProtectedRoute>
                        } />
                        <Route path="*" element={
                            <ProtectedRoute onAuthenticated={onAuthenticated}>
                                {authenticatedLayout}
                            </ProtectedRoute>
                        } />
                    </Routes>
                </Layout>
            </Router>
        </ConfigProvider>
    );
};

const App: React.FC = () => <DesignProvider preset={sportdings} storageKey="theme"><SportdingsApp /></DesignProvider>;

export default App;
